import {useCallback, useEffect, useRef, useState} from 'react';
import {Navigate, useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {PATHS} from 'routes/constants';
import {usePath} from 'hooks';
import {useAuth} from 'hooks/auth/useAuth';
import {useGuestCreation} from 'hooks/auth/useGuestCreation';
import type {MutateMultipleGuestsSettledResult} from 'hooks/auth/useMutateMultipleGuests';
import {useComputedOnboardingDetails} from 'hooks/useComputedOnboardingDetails';
import {useSummary} from 'context/summary';
import {isDegradedGuestResult} from 'pages/Auth/utils';
import {
  PropertyLinkFrame,
  PropertyLinkShell,
} from 'pages/SearchReservation/components/PropertyLinkShell';
import {useInstantCheckInOffer} from './useInstantCheckInOffer';
import {useStepTransition} from './useStepTransition';
import {FinalScreen} from './FinalScreen';
import {WelcomeStep} from './steps/WelcomeStep';
import {CountStep} from './steps/CountStep';
import {EmailStep} from './steps/EmailStep';
import {TripStep} from './steps/TripStep';
import {PickStep, type RegisterRequest} from './steps/PickStep';
import {RegisteringStep, type RegisteringPhase} from './steps/RegisteringStep';
import {LeftStep, type RegisteredGuest} from './steps/LeftStep';

type Step = 'welcome' | 'email' | 'trip' | 'count' | 'pick' | 'registering' | 'left';
type SignInStage = 'auth' | 'trip' | 'count';

type Props = {
  entry?: 'login' | 'createAccount';
};

const SETTLE_MS = 1000;

function InstantCheckInView({entry = 'login'}: Props) {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const {isLoggedIn, isInitialLoading: isAuthLoading} = useAuth();
  const {
    shouldAskReservationGroup,
    shouldAskNumberOfGuests,
    areGuestCreated,
    remainingAllowedGuest,
    isLoadingDetails,
  } = useComputedOnboardingDetails();
  const {markDone} = useInstantCheckInOffer();
  const {isInstantCheckInEnabled} = useSummary();

  const [step, setStep] = useState<Step | null>(null);
  const [isFinal, setIsFinal] = useState(false);
  const [isNothingToDo, setIsNothingToDo] = useState(false);
  const [phase, setPhase] = useState<RegisteringPhase>('sending');
  const [registered, setRegistered] = useState<RegisteredGuest[]>([]);
  const [failedNames, setFailedNames] = useState<string[]>([]);
  const [pending, setPending] = useState<RegisterRequest | null>(null);
  const timers = useRef<number[]>([]);
  const {columnRef, ghostLayerRef, captureCurrentStep} = useStepTransition(step);

  const goTo = useCallback(
    (next: Step) => {
      captureCurrentStep();
      setStep(next);
    },
    [captureCurrentStep],
  );

  const resolveNextStep = useCallback(
    (after: SignInStage): Step | null => {
      if (after === 'auth' && shouldAskReservationGroup && !areGuestCreated) {
        return 'trip';
      }
      if (after !== 'count' && shouldAskNumberOfGuests && !areGuestCreated) {
        return 'count';
      }
      if (isInstantCheckInEnabled && (after === 'count' || remainingAllowedGuest > 0)) {
        return 'pick';
      }
      return null;
    },
    [
      shouldAskReservationGroup,
      shouldAskNumberOfGuests,
      areGuestCreated,
      isInstantCheckInEnabled,
      remainingAllowedGuest,
    ],
  );

  useEffect(
    function chooseFirstStep() {
      if (step || isNothingToDo || isAuthLoading || isLoadingDetails) return;
      if (!isLoggedIn) {
        setStep(entry === 'createAccount' ? 'email' : 'welcome');
        return;
      }

      const next = resolveNextStep('auth');
      if (next) {
        setStep(next);
        return;
      }
      setIsNothingToDo(true);
    },
    [
      step,
      isNothingToDo,
      isAuthLoading,
      isLoadingDetails,
      isLoggedIn,
      entry,
      resolveNextStep,
    ],
  );

  useEffect(() => () => timers.current.forEach(window.clearTimeout), []);

  const later = useCallback((callback: () => void, ms: number) => {
    timers.current.push(window.setTimeout(callback, ms));
  }, []);

  const handleFinish = useCallback(() => {
    markDone();
    setIsFinal(true);
  }, [markDone]);

  const handleContinueAfter = useCallback(
    (stage: SignInStage) => {
      const next = resolveNextStep(stage);
      if (next) {
        goTo(next);
        return;
      }
      handleFinish();
    },
    [resolveNextStep, goTo, handleFinish],
  );

  const handleRegistered = useCallback(
    ({success, failed, threw}: MutateMultipleGuestsSettledResult) => {
      if (threw || success.length === 0) {
        setPhase('error');
        return;
      }

      const guests = success.map(result => ({
        id: result.data.id,
        name: [result.data.name, result.data.surname].filter(Boolean).join(' '),
        isComplete: !isDegradedGuestResult(result),
      }));
      setRegistered(guests);
      setFailedNames(failed.flatMap(({item}) => item.full_name?.trim() || []));
      markDone();
      setPhase('confirmed');
      later(() => {
        if (guests.some(guest => !guest.isComplete) || failed.length > 0) {
          goTo('left');
          return;
        }
        setIsFinal(true);
      }, SETTLE_MS);
    },
    [goTo, markDone, later],
  );

  const {createGuests, guestErrors} = useGuestCreation({
    participants: pending?.participants,
    onRegistered: handleRegistered,
  });

  const handleRegister = (request: RegisterRequest) => {
    setPending(request);
    setPhase('sending');
    goTo('registering');
    createGuests({
      participants: request.participants,
      leaderGuestId: request.leaderId,
      guestsData: request.guestsData,
    });
  };

  const handleOpenBooking = useCallback(
    () => navigate(getFullPath(PATHS.home), {replace: true}),
    [navigate, getFullPath],
  );

  const handleOpenGuestForm = () => {
    markDone();
    navigate(getFullPath(PATHS.addPersonalDataForm));
  };

  const handleOpenGuestList = () => {
    markDone();
    navigate(getFullPath(PATHS.guestRegistration));
  };

  const who =
    pending?.participants.length === 1
      ? pending.participants[0].name
      : t('guests_count', {count: pending?.participants.length ?? 0});

  const renderStep = () => {
    switch (step) {
      case 'welcome':
        return (
          <WelcomeStep
            columnRef={columnRef}
            onSignedIn={() => handleContinueAfter('auth')}
            onEmail={() => goTo('email')}
            onSkip={handleFinish}
          />
        );
      case 'email':
        return (
          <EmailStep
            columnRef={columnRef}
            onSignedIn={() => handleContinueAfter('auth')}
            onBack={() => goTo('welcome')}
          />
        );
      case 'trip':
        return (
          <TripStep columnRef={columnRef} onDone={() => handleContinueAfter('trip')} />
        );
      case 'count':
        return (
          <CountStep columnRef={columnRef} onDone={() => handleContinueAfter('count')} />
        );
      case 'pick':
        return (
          <PickStep
            columnRef={columnRef}
            onRegister={handleRegister}
            onAddGuest={handleOpenGuestForm}
            onLater={handleFinish}
          />
        );
      case 'registering':
        return (
          <RegisteringStep
            columnRef={columnRef}
            phase={phase}
            who={who}
            registeredCount={registered.length}
            guestErrors={guestErrors}
            onRetry={() => goTo('pick')}
            onUseForm={handleOpenGuestForm}
          />
        );
      case 'left':
        return (
          <LeftStep
            columnRef={columnRef}
            guests={registered}
            failedNames={failedNames}
            guestErrors={guestErrors}
            onComplete={handleOpenGuestList}
            onAddGuest={handleOpenGuestForm}
            onLater={handleFinish}
          />
        );
      default:
        return <PropertyLinkShell skeleton />;
    }
  };

  if (isNothingToDo) {
    return <Navigate to={getFullPath(PATHS.home)} replace />;
  }

  return (
    <PropertyLinkFrame>
      <div
        ref={ghostLayerRef}
        aria-hidden
        className="pointer-events-none fixed inset-0"
      />
      {renderStep()}
      {isFinal && <FinalScreen registered={registered} onFinished={handleOpenBooking} />}
    </PropertyLinkFrame>
  );
}

export {InstantCheckInView};
